import { createContext, useState, useEffect, ReactNode, useContext, useCallback, useRef } from "react"
import { CurrenciesAllowedEnum } from "@tutellus/core-contracts-ifaces"
import { ErrorModal, ErrorModalProps } from "../components/modules/Modals/ErrorModal"
import { DomainEventDetail, DomainEvents } from "../domain/_kernel/Events"
import { Toaster, ToasterHandle, ToastProps } from "@tutellus/tutellus-components"
import { ErrorCodes } from "../domain/_kernel/ErrorCodes"
import { useLocalStorage } from "../hooks/useLocalStorage"

const THEME_MODE_STORAGE_KEY = "theme-mode"
export const CURRENCY_STORAGE_KEY = "currency"

export enum ThemeMode {
  LIGHT = "light",
  DARK = "dark",
}

export enum ModalTypes {
  ERROR = "ERROR",
  SOCIAL = "SOCIAL",
}

export type ModalType = (typeof ModalTypes)[keyof typeof ModalTypes]

export interface ModalConfigMap {
  [ModalTypes.ERROR]: {
    component: typeof ErrorModal
    props: ErrorModalProps
  }
}

interface ModalInstance<T extends ModalType = ModalType> {
  type: T
  props: T extends keyof ModalConfigMap ? Omit<ModalConfigMap[T]["props"], "onClose"> : never
}

interface LayoutContextInterface {
  themeMode: ThemeMode
  setThemeMode: (mode: ThemeMode) => void
  currencyID: CurrenciesAllowedEnum
  setCurrencyID: (currency: CurrenciesAllowedEnum) => void
  addToast: (toast: Omit<ToastProps, "id" | "isExiting" | "onClose"> & { duration?: number }) => void
  removeToast: (id: string) => void
  clearAllToasts: () => void
}

const initialState: LayoutContextInterface = {
  themeMode: ThemeMode.DARK,
  setThemeMode: () => {},
  currencyID: CurrenciesAllowedEnum.USD,
  setCurrencyID: () => {},
  addToast: () => {},
  removeToast: () => {},
  clearAllToasts: () => {},
}

interface LayoutProviderProps {
  children: ReactNode
}

const Context = createContext<LayoutContextInterface>(initialState)

export const LayoutContext = ({ children }: LayoutProviderProps) => {
  const [themeMode, setThemeMode] = useLocalStorage<ThemeMode>(THEME_MODE_STORAGE_KEY, ThemeMode.DARK)
  const [currencyID, setCurrencyID] = useLocalStorage<CurrenciesAllowedEnum>(CURRENCY_STORAGE_KEY,CurrenciesAllowedEnum.USD) // prettier-ignore
  const [activeModal, setActiveModal] = useState<ModalInstance | null>(null)
  const toasterRef = useRef<ToasterHandle>(null)

  const addToast = useCallback((toast: Omit<ToastProps, "id" | "isExiting" | "onClose"> & { duration?: number }) =>toasterRef.current?.addToast({ ...toast, onClose: () => {} }),[]) // prettier-ignore
  const removeToast = useCallback((id: string) => toasterRef.current?.removeToast(id), [])
  const clearAllToasts = useCallback(() => toasterRef.current?.clearAllToasts(), [])

  const openModal = <T extends keyof ModalConfigMap>(type: T, props: Omit<ModalConfigMap[T]["props"], "onClose">) =>
    setActiveModal({ type, props })

  const closeModal = () => setActiveModal(null)

  const handleDomainError = useCallback((e: CustomEvent<DomainEventDetail[DomainEvents.ERROR]>) => {
    switch (e.detail.domainError.code) {
      case ErrorCodes.SIGNATURE_ERROR:
      case ErrorCodes.TRANSACTION_ERROR:
        openModal(ModalTypes.ERROR, { error: e.detail.domainError.error })
        break
    }
  }, [])

  // El tema va en <html>: los portales (modales, toasts) cuelgan fuera de #root
  useEffect(() => {
    document.documentElement.classList.remove(ThemeMode.LIGHT, ThemeMode.DARK)
    document.documentElement.classList.add(themeMode)
  }, [themeMode])

  // Currency

  useEffect(() => {
    window.addEventListener(DomainEvents.ERROR, handleDomainError as EventListener)

    return () => {
      window.removeEventListener(DomainEvents.ERROR, handleDomainError as EventListener)
    }
  }, [handleDomainError])

  const renderModal = () => {
    if (!activeModal) return null

    switch (activeModal.type) {
      case ModalTypes.ERROR:
        return <ErrorModal {...(activeModal.props as Omit<ErrorModalProps, "onClose">)} onClose={closeModal} />
    }
  }

  return (
    <Context.Provider
      value={{
        themeMode,
        setThemeMode,
        currencyID,
        setCurrencyID,
        addToast,
        removeToast,
        clearAllToasts,
      }}
    >
      {renderModal()}
      <Toaster ref={toasterRef} />
      {children}
    </Context.Provider>
  )
}

export const useLayout = function () {
  const context = useContext(Context)
  if (context === undefined) {
    throw new Error(`useLayout must be used within a LayoutContextProvider`)
  }
  return context
}
